import React, { useState } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import {
  FiBarChart2, FiMapPin, FiExternalLink, FiHeart, FiAlertTriangle, FiChevronDown, FiChevronRight, FiInfo,
} from 'react-icons/fi'
import { FaHeart } from 'react-icons/fa'
import Flag from './CountryFlag'
import importCostsData from '../data/import_costs.json'
import PricingExplanationPopup from './PricingExplanationPopup'
import BpmExplanationPopup from './BpmExplanationPopup'
import PurchasePlanner from './PurchasePlanner'
import { toFlagCode, countryName } from '../lib/countries.js'
import { verdictOf } from '../lib/verdict.js'
import { pickKind, profitFigures, netProfit, adLink, nlComparablesUrl } from '../lib/picks.js'
import { hasPricingEvidence, valuationStatus } from '../lib/valuationStatus.js'
import { freshnessPresentation } from '../lib/freshnessPresentation.js'
import { recommendationEvidence, evidenceTimestamp, sourceCheckMaxAgeMs } from '../lib/recommendationEvidence.js'
import { useEvidenceClock } from '../lib/useEvidenceClock.js'
import { sellerTypeLabel } from '../lib/sellerType.js'
import { reasonText, storyText } from '../lib/reasonText.js'
import './CarCard.css'

const eur = value => (Number.isFinite(value)
  ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value)
  : '—')
const formatPnlPrice = price => new Intl.NumberFormat('nl-NL', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(price)
// The sign goes before the currency symbol, so a range reads "-€ 1.200 … € 3.400".
const formatPriceForRange = price => `${price < 0 ? '-' : ''}${eur(Math.abs(price))}`
const capitalize = text => String(text).replace(/(^|\s)(\S)/g, (_, space, letter) => space + letter.toUpperCase())

const KIND_LABEL = { koopkans: 'Buy opportunity', controleer: 'Check it yourself', geen: 'Not a pick' }
// The pipeline stores AutoUncle's Dutch fuel words and English body words; the card speaks t().
const FUEL_LABEL = { benzine: 'Petrol', diesel: 'Diesel', hybride: 'Hybrid', elektrisch: 'Electric' }
const BODY_LABEL = {
  suv: 'SUV', hatchback: 'Hatchback', sedan: 'Sedan', saloon: 'Sedan', berlina: 'Sedan',
  cabriolet: 'Convertible', convertible: 'Convertible', spider: 'Convertible', 'coupé': 'Coupé', coupe: 'Coupé',
  van: 'Van / MPV', kombi: 'Estate', wagon: 'Estate', break: 'Estate', estate: 'Estate', pickup: 'Pick-up', compact: 'Compact',
}

/** SIMPLE-1: one label, the money, the confidence, every spec the source gives, the full P&L. */
function CarCard({ car, isSaved, onToggleSave, isSavedSnapshot = false }) {
  const { t, lang } = useTranslation()
  const sourceAt = evidenceTimestamp(car.source_checked_at)
  const clock = useEvidenceClock(sourceAt === null ? null : sourceAt + sourceCheckMaxAgeMs)
  const recommendationCurrent = !isSavedSnapshot && recommendationEvidence(car.recommendation_evidence, clock).current
  const verdict = verdictOf(car, { recommendationCurrent })
  const kind = pickKind(verdict.key)
  const { expected, safe } = profitFigures(car)
  const ad = adLink(car)
  const freshness = freshnessPresentation(car, t, clock)
  const valid = car.economics_valid === true
  const seller = sellerTypeLabel(car.seller_type)
  const valuation = valuationStatus(car)
  const [expandedPnL, setExpandedPnL] = useState(false)
  const [showPricingPopup, setShowPricingPopup] = useState(false)
  const [showBpmPopup, setShowBpmPopup] = useState(false)
  const [vatIncluded, setVatIncluded] = useState(true)
  const [expandedCostCategory, setExpandedCostCategory] = useState(null)

  const otherTrim = String(car.match_story || '').match(/all comparables are other trims than (.+)$/)?.[1] || null
  // The verifier refuses a pick whose dealer price rose > 5%; a smaller rise keeps it, so say how
  // much more the dealer asks now than the price these figures were computed on.
  const priceRise = car.source_price > 0 && car.price_it > 0 ? car.source_price - car.price_it : 0
  const comps = Number(car.match_count) || 0
  // Facts a dealer wants before driving there; each shows only when it applies.
  const warnings = [
    priceRise >= 1 && t('The dealer now asks {price} ({rise} more): subtract that from the profit above',
      { price: eur(car.source_price), rise: eur(priceRise) }),
    // pricer_core writes this clause when every comparable is another trim. Measured on 32,795
    // cohort backtests (age 1-8): such values are > 25% too high 9.1% of the time, same-trim 2.8%.
    kind !== 'geen' && otherTrim && t('Valued only on other trims than {trim}: check Dutch adverts for this trim yourself', { trim: otherTrim }),
    kind !== 'geen' && comps > 0 && comps < 5 &&
      t('Only {count} comparable Dutch cars: check the Dutch adverts yourself', { count: comps }),
    car.days_online > 60 && t('Online for {days} days', { days: Math.round(car.days_online) }),
    String(car.bpm_co2_source || '').startsWith('imputed') && t('BPM uses an estimated CO2 figure'),
    car.price_flag_anomaly && t('Price anomaly'),
  ].filter(Boolean)

  // Confidence in the valuation: the calibrated evidence score, its published band and the error
  // measured for this kind of match. It ranks evidence strength; it is not a probability.
  const confidenceScore = car.confidence_score || 0
  const confidenceClass = confidenceScore >= 0.9
    ? 'confidence-very-high'
    : confidenceScore >= 0.75
      ? 'confidence-high'
      : confidenceScore >= 0.45
        ? 'confidence-medium'
        : 'confidence-low'
  const publishedRangeSummary = car.sale_range_available &&
    Number.isFinite(car.sale_range_downside_pct) &&
    Number.isFinite(car.sale_range_upside_pct)
    ? t('Published 80% range: -{down}% / +{up}%', {
        down: car.sale_range_downside_pct.toFixed(1),
        up: car.sale_range_upside_pct.toFixed(1),
      })
    : t('Published range unavailable')

  // Everything the source listing states, the way a car site lists it. PocketBase reads an unset
  // number as 0, so 0 means "not stated" for every field here.
  const rarity = car.model_rarity_score > 0 ? car.model_rarity_score : null
  const [rarityLabel, rarityClass] = rarity === null ? [null, null]
    : rarity >= 0.8 ? ['Very Rare', 'rarity-very-rare']
      : rarity >= 0.6 ? ['Rare', 'rarity-rare']
        : rarity < 0.4 ? ['Common', 'rarity-common'] : ['Uncommon', 'rarity-average']
  const listed = car.listed_since ? new Date(String(car.listed_since).replace(' ', 'T')) : null
  const locale = lang === 'nl' ? 'nl-NL' : 'en-GB'
  const specs = [
    ['Year', car.year || null],
    ['Mileage', car.mileage > 0 ? `${car.mileage.toLocaleString('nl-NL')} km` : null],
    ['Fuel', car.fuel ? t(FUEL_LABEL[car.fuel] || car.fuel) : null],
    ['Transmission', car.transmission ? t(car.transmission) : null],
    ['Power', car.power > 0
      ? `${car.power} ${t('hp')}${car.horsepower_kw > 0 ? ` (${Math.round(car.horsepower_kw)} kW)` : ''}` : null],
    ['Engine', car.engine_l > 0 ? `${car.engine_l.toLocaleString(locale, { minimumFractionDigits: 1 })} l` : null],
    ['Body Type', car.body_type ? t(BODY_LABEL[car.body_type] || capitalize(car.body_type)) : null],
    ['CO2', car.co2 > 0 ? `${Math.round(car.co2)} g/km` : null],
    ['Variant', car.variant ? capitalize(car.variant) : null],
    ['Seller', seller ? t(seller === 'Particulier' ? 'Private' : 'Dealer') : null],
    ['Platform', car.platform || null],
    ['Online since', listed && !Number.isNaN(listed.getTime())
      ? listed.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : null],
    ['Price change', car.price_change_percent ? `${car.price_change_percent > 0 ? '+' : ''}${car.price_change_percent}%` : null],
    ['AutoUncle rating', car.deal_category ? t(car.deal_category) : null],
    ['Distance to NL', car.transport_distance_km > 0
      ? `${Math.round(car.transport_distance_km).toLocaleString('nl-NL')} km` : null],
  ].filter(([, value]) => value !== null && value !== '')
  const foreignPrice = car.price_currency_original && car.price_currency_original !== 'EUR' && car.price_amount_original > 0
    ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: car.price_currency_original, maximumFractionDigits: 0 })
      .format(car.price_amount_original)
    : null

  // COST-DETAIL-1 (2026-08-17): restore the per-item explanation of the import
  // costs — what each fee IS, and the official source you can check it against.
  // It was dropped when the card moved to served-contract totals, because the
  // old version SUMMED these reference items into the category totals and could
  // therefore disagree with the figure the pipeline actually used (the COST-1
  // double-count). So this is explanation only: totals continue to come from the
  // per-car served fields, and nothing here feeds arithmetic. Reference rates are
  // labelled as such, since the real per-car cost varies with distance and value.
  const costItemsByCategory = (() => {
    const cc = car.country_code?.toUpperCase()
    if (!cc || cc === 'NL') return {}
    const source = importCostsData[cc]
    if (!source) return {}
    const isEu = source.eu_member !== false
    const isEfta = source.efta_member === true
    const out = {}
    const add = (item, origin) => {
      const id = item.id || ''
      // NAKS is a non-EU customs formality; showing it on an EU import would
      // describe a fee that is never charged.
      if (id.includes('naks') && (isEu || isEfta)) return
      if (item.cost_type === 'ongoing') return
      const cat = item.category || ''
      ;(out[cat] = out[cat] || []).push({
        id: `${origin}-${id}`,
        name: item.short_name || id,
        description: item.description || '',
        url: item.government_url || item.provider_url || '',
        cost: item.estimated_cost_eur ?? 0,
        min: item.min_eur,
        max: item.max_eur,
        isRefund: (item.estimated_cost_eur ?? 0) < 0,
      })
    }
    ;(source.cost_items || []).forEach(i => add(i, 'src'))
    ;(importCostsData.NL?.cost_items || []).forEach(i => add(i, 'nl'))
    return out
  })()

  // Which reference items explain which line on the card.
  const COST_CATEGORY_ITEMS = {
    insurance: [
      ['insurance_transit_wa', t('Transit Liability (WA)')],
      ['insurance_green_card', t('Green Card')],
      ['insurance_cargo', t('Cargo Insurance')],
    ],
    rdw: [
      ['import_registration', t('Registration')],
      ['inspection_nl', t('Inspection')],
      ['insurance_nl', t('Temporary Insurance')],
    ],
    other: [
      ['export', t('Export')],
      ['inspection_source', t('Source Country Inspection')],
      ['documentation_source', t('Documentation')],
      ['transit_crossing', t('Route tolls, bridges & ferries')],
    ],
  }

  const groupedCostItems = (catKey) => (COST_CATEGORY_ITEMS[catKey] || [])
    .map(([key, label]) => ({ label, items: costItemsByCategory[key] || [] }))
    .filter(group => group.items.length > 0)

  /** One cost line that expands into what the fee is, and where to verify it. */
  const CostCategoryRow = ({ catKey, label, value }) => {
    const groups = groupedCostItems(catKey)
    const expandable = groups.length > 0
    const isOpen = expandedCostCategory === catKey
    return (
      <>
        <div
          className="pnl-row sub-cost"
          style={{ cursor: expandable ? 'pointer' : 'default' }}
          onClick={() => expandable && setExpandedCostCategory(isOpen ? null : catKey)}
          title={expandable ? t('Click to see what this covers') : undefined}
        >
          <span className="pnl-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {label}
            {expandable && <FiInfo size={12} style={{ color: '#94a3b8', flexShrink: 0 }} />}
          </span>
          <span className="pnl-value">-{formatPnlPrice(value)}</span>
        </div>
        {isOpen && (
          <div className="cost-detail">
            {groups.map(group => (
              <div key={group.label}>
                <div className="cost-detail-group">{group.label}</div>
                {group.items.map(item => (
                  <div key={item.id} className="cost-detail-item">
                    <div className="cost-detail-text">
                      <div className="cost-detail-name">{item.name}</div>
                      {item.description && (
                        <div className="cost-detail-desc">{item.description}</div>
                      )}
                      {item.url && (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="cost-detail-link"
                        >
                          <FiExternalLink size={10} /> {t('Official source')}
                        </a>
                      )}
                    </div>
                    <span className="cost-detail-cost">
                      {item.isRefund
                        ? t('refundable')
                        : item.cost > 0
                          ? `€${Number(item.cost).toLocaleString('nl-NL', { maximumFractionDigits: 0 })}`
                          : '—'}
                    </span>
                  </div>
                ))}
              </div>
            ))}
            <div className="cost-detail-note">
              {t('Reference rates for this route. The amount above is calculated for this specific car, so it can differ with distance and value.')}
            </div>
          </div>
        )}
      </>
    )
  }

  const getCountryCode = (country, countryCode) => {
    // Use country_code if available, otherwise derive from country name
    if (countryCode) {
      return countryCode.toUpperCase()
    }
    const codeMap = {
      'Italy': 'IT',
      'Germany': 'DE',
      'France': 'FR',
      'Spain': 'ES',
      'Netherlands': 'NL',
      'Belgium': 'BE',
      'Austria': 'AT',
      'Switzerland': 'CH',
      'UK': 'GB',
      'United Kingdom': 'GB'
    }
    return codeMap[country] || 'EU'
  }

  const economicsAvailable = valid
  const vatDeductibleAmountRaw = economicsAvailable ? car.vat_deductible_amount : 0
  const profitInclVatRaw = economicsAvailable ? car.estimated_margin : null
  const profitExclVatRaw = economicsAvailable ? car.expected_revenue_excl_vat : null
  const activeProfitRaw = vatIncluded ? profitInclVatRaw : profitExclVatRaw
  const hasVatDifference = economicsAvailable && vatDeductibleAmountRaw > 0

  const pnlProfitColor = !economicsAvailable ? '#64748b' : activeProfitRaw > 0 ? '#14b8a6' : '#e11d48'

  // Calculate profit range based on sales range
  // IMPORTANT: This must use the same import costs calculation as expected_revenue
  // expected_revenue = pred_target_price - purchasePrice - importCosts
  // So profit range = sales_range - purchasePrice - importCosts
  const calculateProfitRange = () => {
    if (!economicsAvailable || !car.sale_range_available) return null
    const totalCosts = car.total_costs_incl_vat

    const saleLo = car.pred_sale_lo
    const saleHi = car.pred_sale_hi

    const profitLo = saleLo - totalCosts
    const profitHi = saleHi - totalCosts
    return { profitLo, profitHi }
  }

  const profitRangeRaw = calculateProfitRange()
  // Adjust profit range for VAT toggle
  const profitRangeVat = profitRangeRaw && !vatIncluded && vatDeductibleAmountRaw > 0
    ? { profitLo: profitRangeRaw.profitLo + vatDeductibleAmountRaw, profitHi: profitRangeRaw.profitHi + vatDeductibleAmountRaw }
    : profitRangeRaw
  // All displayed values come from the contracted row that passed the shared identities.
  const transportCostDisplay = economicsAvailable ? car.transport_cost_eur : null
  const insuranceCostDisplay = economicsAvailable ? car.insurance_cost : null
  const rdwCostDisplay = economicsAvailable ? car.rdw_registration_cost : null
  const otherCostsDisplay = economicsAvailable ? car.other_costs : null
  const customsTotalDisplay = economicsAvailable ? car.customs_total : null
  const bpmCostDisplay = economicsAvailable ? car.bpm_rest : null
  const marginPctInclVat = economicsAvailable ? car.margin_pct : null
  const marginPctExclVat = economicsAvailable ? car.expected_margin_pct_excl_vat * 100 : null
  const activeProfit = activeProfitRaw
  const activeMarginPct = vatIncluded ? marginPctInclVat : marginPctExclVat
  const activeTotalCosts = vatIncluded ? car.total_costs_incl_vat : car.total_costs_excl_vat
  const nonCustomsImportCosts = economicsAvailable ? car.import_fixed_costs - customsTotalDisplay : null

  // SIMPLE-1: from the gross margin to the two profit figures at the top of the card, on the
  // rows shown, so the P&L adds up to them (to the euro: the stored figures round once).
  const pointValue = car.estimated_sales_price_nl
  const { expected: netExpected, safe: netSafe, haircut: saleHaircut, reserve: conditionReserve } =
    netProfit(car, economicsAvailable ? activeProfit : null)
  const netColor = netExpected > 0 ? '#14b8a6' : '#e11d48'

  return (
    <article className={`car-card pick-${kind}`}>
      <div className="card-image-section">
        <img src={car.image || '/placeholder-car.svg'} alt={`${car.make} ${car.model}`} className="car-image"
          loading="lazy" onError={e => { if (!e.currentTarget.src.endsWith('/placeholder-car.svg')) e.currentTarget.src = '/placeholder-car.svg' }} />
        {car.country_code && <span className="country-flag-icon"><Flag code={toFlagCode(car.country_code)} /></span>}
        <span className={`pick-badge pick-badge-${kind}`}>{t(KIND_LABEL[kind])}</span>
        {onToggleSave && (
          <button className={`save-button ${isSaved ? 'saved' : ''}`} onClick={() => onToggleSave(car)}
            title={isSaved ? t('Remove from saved') : t('Save listing')} aria-pressed={Boolean(isSaved)}>
            {isSaved ? <FaHeart /> : <FiHeart />}
          </button>
        )}
      </div>

      <div className="card-content">
        <h3 className="car-title">
          {car.make} {car.model}
          {car.variant && car.variant_source === 'taxonomy_exact' && <span className="car-trim"> {car.variant}</span>}
        </h3>
        <p className="car-specs">
          {[car.year, car.mileage > 0 && `${car.mileage.toLocaleString('nl-NL')} km`, car.fuel && t(FUEL_LABEL[car.fuel] || car.fuel),
            car.power > 0 && `${car.power} ${t('hp')}`, car.transmission && t(car.transmission)].filter(Boolean).join(' · ')}
        </p>
        <p className="car-place">
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            [car.location, countryName(car.country_code)].filter(Boolean).join(', '))}`}
          target="_blank" rel="noopener noreferrer">
            <FiMapPin /> {[car.location, t(countryName(car.country_code))].filter(Boolean).join(', ')}
          </a>
          {seller && <span> · {t(seller === 'Particulier' ? 'Private' : 'Dealer')}</span>}
          {car.days_online > 0 && <span> · {t('{days} days online', { days: Math.round(car.days_online) })}</span>}
        </p>

        {kind === 'geen' && <p className="pick-why-not">{t(verdict.label)}{verdict.why ? `: ${reasonText(t(verdict.why), lang)}` : ''}</p>}
        {kind === 'controleer' && <p className="pick-check">{reasonText(t(verdict.why || 'Open the advert and check price, condition and location yourself.', verdict.vars), lang)}</p>}

        <dl className="money">
          <div><dt>{t('Asking price')}</dt><dd>{eur(car.price_it)}{foreignPrice && <small>{foreignPrice}</small>}</dd></div>
          <div><dt>{t('Total cost landed in NL')}</dt><dd>{valid ? eur(car.total_costs_incl_vat) : '—'}</dd></div>
          <div>
            <dt>{t('Dutch resale value')}</dt>
            {pointValue > 0 ? (
              <dd>{eur(pointValue)}
                {car.sale_range_available
                  ? <small> ({eur(car.pred_sale_lo)} – {eur(car.pred_sale_hi)})</small>
                  : <small>{t('Range unavailable')}</small>}
              </dd>
            ) : (
              // VALUATION-STATUS-1: a finished refusal is an answer, not a wait.
              <dd className="price-no-value" title={valuation.why ? t(valuation.why) : undefined}>
                <small>{t(valuation.short || 'Valuation unavailable')}</small>
              </dd>
            )}
          </div>
        </dl>

        {valid && expected !== null && (
          <div className={`profit-box ${kind === 'geen' ? 'muted' : ''}`}>
            <div className="profit-main">
              <span>{t('Expected profit')}</span>
              <strong className={expected < 0 ? 'neg' : ''}>{eur(expected)}</strong>
            </div>
            {safe !== null && (
              <div className="profit-safe" title={t('Profit if the car only sells at the low end of our Dutch value range, after the same costs and reserves.')}>
                <span>{t('If the sale disappoints')}</span>
                <strong className={safe < 0 ? 'neg' : ''}>{eur(safe)}</strong>
              </div>
            )}
          </div>
        )}
        {!valid && <p className="pick-check">{t('Costs are being re-checked; no profit shown for this car yet.')}</p>}

        {(confidenceScore > 0 || hasPricingEvidence(car)) && (
          <div className="additional-info">
            <div className="analysis-metrics">
              {confidenceScore > 0 && (
                <div className={`info-chip evidence-chip ${confidenceClass}`}>
                  <span className="chip-label">{t('Evidence score')}</span>
                  <span className="chip-value">
                    {(confidenceScore * 100).toFixed(0)} / 100
                    {confidenceScore < 0.5 && (
                      <span className="confidence-warning" title={t('Few similar cars found - price estimate may be less accurate')}>
                        <FiAlertTriangle style={{ width: '14px', height: '14px', color: '#eab308', strokeWidth: 2.5 }} />
                      </span>
                    )}
                    <button
                      className="confidence-info-button"
                      onClick={() => setShowPricingPopup(true)}
                      title={t('View pricing analysis details')}
                      aria-label={t('View pricing analysis details')}
                    >
                      <FiInfo className="icon-inline" />
                    </button>
                  </span>
                  <span className="chip-sub" title={t('This ranks the strength of the pricing evidence. It is not an accuracy probability.')}>
                    {publishedRangeSummary}
                  </span>
                  {car.expected_ape_pct != null && (
                    <span className="chip-sub" title={t('Median error measured for this kind of match (tier, comparables, age) on Dutch cars we could check')}>
                      {t('typical error')} ±{Math.round(car.expected_ape_pct)}%
                    </span>
                  )}
                  {car.match_story && (
                    <span className="chip-sub evidence-story" title={storyText(car.match_story, lang)}>
                      {storyText(car.match_story, lang)}
                    </span>
                  )}
                  <div className="confidence-bar">
                    <div
                      className="confidence-bar-fill"
                      style={{ width: `${Math.round(confidenceScore * 100)}%` }}
                    />
                  </div>
                </div>
              )}
              {/* Only where there IS evidence: a labelled blank on a refused car is a row of nothing. */}
              {hasPricingEvidence(car) && (
                <>
                  <div className="info-chip">
                    <span className="chip-label">{t('Tier')}</span>
                    <span className="chip-value">
                      {car.match_tier && car.match_tier !== 'NA'
                        ? car.match_tier.replace(/^Tier\s*/i, '')
                        : '—'}
                    </span>
                  </div>
                  <div className="info-chip">
                    <span className="chip-label">{t('Basis')}</span>
                    <span className="chip-value" title={comps > 0 ? t('{count} similar cars used for analysis', { count: comps }) : undefined}>
                      {comps > 0 ? comps : car.n}
                    </span>
                    <span className="chip-sub" style={{ color: '#94a3b8' }}>{t('similar')}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {warnings.length > 0 && (
          <ul className="pick-warnings">
            {warnings.map(w => <li key={w}><FiAlertTriangle /> {w}</li>)}
          </ul>
        )}

        <div className="specs-grid">
          {specs.map(([label, value]) => (
            <div className="spec" key={label}>
              <span className="spec-label">{t(label)}</span>
              <span className="spec-value">{value}</span>
            </div>
          ))}
          {rarity !== null && (
            <div className="spec" title={car.model_listing_count > 0
              ? t('{count} of this model listed across Europe', { count: car.model_listing_count.toLocaleString('nl-NL') }) : undefined}>
              <span className="spec-label">{t('Model Rarity')}</span>
              <span className="spec-value"><span className={`rarity-pill ${rarityClass}`}>{t(rarityLabel)}</span></span>
            </div>
          )}
        </div>

        <div className="card-actions">
          {ad && (
            <a className="btn-ad" href={ad.href} target="_blank" rel="noopener noreferrer">
              {ad.direct ? t('View the advert on {host}', { host: ad.host }) : t('View the advert via AutoUncle')} <FiExternalLink />
            </a>
          )}
          <a className="btn-compare" href={nlComparablesUrl(car)} target="_blank" rel="noopener noreferrer">
            {t('Compare in NL')} <FiExternalLink />
          </a>
        </div>

        {/* P&L, as it was before SIMPLE-1 (Coen: "don't touch the P&L"), ending in the two
            profit figures the card leads with. */}
        <div className="pnl-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              className="pnl-toggle"
              onClick={() => setExpandedPnL(!expandedPnL)}
              style={{ flex: 1 }}
              aria-expanded={expandedPnL}
            >
              <span>
                <FiBarChart2 className="icon-inline" />
                {t('Cost and margin breakdown')}
              </span>
              <span className="toggle-icon">
                {expandedPnL ? <FiChevronDown /> : <FiChevronRight />}
              </span>
            </button>

            {/* VAT toggle switch */}
            {hasVatDifference && expandedPnL && (
              <div
                onClick={(e) => { e.stopPropagation(); setVatIncluded(!vatIncluded) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  backgroundColor: vatIncluded ? 'rgba(225, 29, 72, 0.06)' : 'rgba(20, 184, 166, 0.06)',
                  border: `1px solid ${vatIncluded ? 'rgba(225, 29, 72, 0.15)' : 'rgba(20, 184, 166, 0.15)'}`,
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                  userSelect: 'none',
                }}
                title={vatIncluded ? t('Showing prices including VAT (private buyer). Click for excl. VAT (business).') : t('Showing prices excluding VAT (business buyer). Click for incl. VAT (private).')}
              >
                <div style={{
                  position: 'relative',
                  width: '28px',
                  height: '16px',
                  borderRadius: '8px',
                  backgroundColor: vatIncluded ? '#e11d48' : '#14b8a6',
                  transition: 'background-color 0.2s ease',
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '2px',
                    left: vatIncluded ? '2px' : '14px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                  }} />
                </div>
                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: '600',
                  color: vatIncluded ? '#e11d48' : '#14b8a6',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                }}>
                  {vatIncluded ? t('incl. BTW') : t('excl. BTW')}
                </span>
              </div>
            )}
          </div>

          {expandedPnL && (!economicsAvailable ? (
            <div className="pnl-details" role="alert" style={{ color: '#b45309' }}>
              <strong>{t('P&L unavailable')}</strong>
              <div style={{ marginTop: '0.35rem', fontSize: '0.8rem' }}>
                {t('The served cost fields are missing or do not reconcile. No profit or cost estimate is shown.')}
              </div>
            </div>
          ) : (
            <div className="pnl-details">
              {/* Income side */}
              <div className="pnl-row income-row">
                <span className="pnl-label">{t('Estimated Dutch asking price (dealer)')}</span>
                {car.estimated_sales_price_nl > 0 ? (
                  <span className="pnl-value" style={{ color: '#14b8a6' }}>
                    +{formatPnlPrice(car.estimated_sales_price_nl)}
                  </span>
                ) : (
                  <span className="pnl-value" style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    {t('Not calculated')}
                  </span>
                )}
              </div>

              {/* Sales Range */}
              {car.sale_range_available && (
                <div className="pnl-row" style={{ fontSize: '0.85rem', padding: '0.4rem 0', marginTop: '0.5rem', color: '#64748b' }}>
                  <span className="pnl-label">{t('Asking-price range (low - high)')}</span>
                  <span className="pnl-value" style={{ fontWeight: '500' }}>
                    {formatPnlPrice(car.pred_sale_lo)} - {formatPnlPrice(car.pred_sale_hi)}
                  </span>
                </div>
              )}

              {car.sale_range_width_pct > 0 && (
                <div className="pnl-row" style={{ fontSize: '0.8rem', padding: '0.25rem 0', color: '#94a3b8' }}>
                  <span className="pnl-label">{t('Range Width')}</span>
                  <span className="pnl-value" style={{ fontWeight: '500' }}>
                    {`${car.sale_range_width_pct.toFixed(1)}%`}
                  </span>
                </div>
              )}
              {!car.sale_range_available && (
                <div className="pnl-row" style={{ fontSize: '0.8rem', padding: '0.25rem 0', color: '#b45309' }}>
                  <span className="pnl-label">{t('Asking-price range')}</span>
                  <span className="pnl-value">{t('Unavailable')}</span>
                </div>
              )}


              {car.confidence_score > 0 && (
                <div
                  className="pnl-row clickable-row"
                  style={{
                    fontSize: '0.8rem',
                    padding: '0.25rem 0',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => setShowPricingPopup(true)}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                  title={t('Click to view confidence calculation breakdown')}
                >
                  <span className="pnl-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {t('Confidence')} <FiInfo style={{ strokeWidth: 2.5 }} />
                  </span>
                  <span className="pnl-value" style={{ fontWeight: '500', borderBottom: '1px dotted currentColor' }}>
                    {(car.confidence_score * 100).toFixed(1)}%
                  </span>
                </div>
              )}

              {/* Market Velocity Section - Refined Design */}
              {(car.est_days_to_sell >= 1 || (car.model_days_median >= 1 && car.model_days_count > 0)) && (
                <div style={{
                  marginTop: '0.75rem',
                  marginBottom: '0.75rem',
                  padding: '0.75rem 0.875rem',
                  backgroundColor: 'rgba(180, 140, 90, 0.04)',
                  border: '1px solid rgba(180, 140, 90, 0.1)',
                  borderRadius: '6px',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '1px solid rgba(180, 140, 90, 0.08)'
                  }}>
                    <div style={{
                      fontSize: '0.65rem',
                      fontWeight: '700',
                      color: '#b48c5a',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {t('Observed listing age')}
                    </div>
                  </div>

                  {car.est_days_to_sell >= 1 && (
                    <div className="pnl-row" style={{ fontSize: '0.8rem', padding: '0.25rem 0', color: '#94a3b8' }}>
                      <span className="pnl-label">{t('Avg. Days Online (Matched Specs)')}</span>
                      <span className="pnl-value" style={{ fontWeight: '500' }}>{t('{days} days', { days: Math.round(car.est_days_to_sell) })}</span>
                    </div>
                  )}

                  {car.model_days_median >= 1 && car.model_days_count > 0 && (
                    <div className="pnl-row" style={{ fontSize: '0.8rem', padding: '0.25rem 0', color: '#94a3b8' }}>
                      <span className="pnl-label">{t('Avg. Days Online (All {model})', { model: car.model })}</span>
                      <span className="pnl-value" style={{ fontWeight: '500' }}>{t('{days} days', { days: Math.round(car.model_days_median) })}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Costs */}
              <div className="pnl-costs">
                <div className="pnl-row cost-row">
                  <span className="pnl-label">{t('Purchase Price')} ({getCountryCode(car.country, car.country_code)})</span>
                  <span className="pnl-value" style={{ color: '#e11d48' }}>
                    -{formatPnlPrice(car.price_it)}
                  </span>
                </div>

                <div className="pnl-breakdown">
                  <div
                    className="pnl-row sub-cost bpm-row"
                    style={{ cursor: car.bpm_calculation_notes ? 'pointer' : 'default' }}
                    onClick={() => car.bpm_calculation_notes && setShowBpmPopup(true)}
                    title={car.bpm_calculation_notes ? t('Click to view BPM calculation details') : undefined}
                  >
                    <span className="pnl-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {t('BPM Tax (NL)')}
                      {car.bpm_calculation_notes && <FiInfo style={{ strokeWidth: 2.5, color: '#94a3b8' }} size={13} />}
                    </span>
                    <span className="pnl-value" style={car.bpm_calculation_notes ? { borderBottom: '1px dotted #94a3b8' } : {}}>
                      -{formatPnlPrice(bpmCostDisplay)}
                    </span>
                  </div>

                  {/* Import Fixed Costs as a category/subtotal */}
                  <div className="pnl-import-category">
                    <div className="pnl-row sub-cost category-header">
                      <span className="pnl-label">{t('Import Costs')}</span>
                      <span className="pnl-value category-total">-{formatPnlPrice(nonCustomsImportCosts)}</span>
                    </div>
                    <div className="pnl-sub-items">
                      <div className="pnl-row sub-cost">
                        <span className="pnl-label">{t('Transport Cost')}</span>
                        <span className="pnl-value">-{formatPnlPrice(transportCostDisplay)}</span>
                      </div>

                      <CostCategoryRow
                        catKey="insurance"
                        label={t('Insurance (transit + cargo)')}
                        value={insuranceCostDisplay}
                      />
                      <CostCategoryRow
                        catKey="rdw"
                        label={t('NL Registration & APK')}
                        value={rdwCostDisplay}
                      />
                      <CostCategoryRow
                        catKey="other"
                        label={t('Export, documents & crossings')}
                        value={otherCostsDisplay}
                      />
                    </div>
                  </div>

                  {/* Customs costs for non-EU imports (UK, CH) */}
                  {customsTotalDisplay > 0 && (
                    <div className="pnl-import-category">
                      <div className="pnl-row sub-cost category-header">
                        <span className="pnl-label">{t('Customs (non-EU)')}</span>
                        <span className="pnl-value category-total" style={{ color: '#e11d48' }}>-{formatPnlPrice(customsTotalDisplay)}</span>
                      </div>
                      <div className="pnl-sub-items">
                        {parseFloat(car.customs_duty) > 0 && (
                          <div className="pnl-row sub-cost">
                            <span className="pnl-label">{t('Import Duty (10%)')}</span>
                            <span className="pnl-value">-{formatPnlPrice(car.customs_duty)}</span>
                          </div>
                        )}
                        {parseFloat(car.customs_btw) > 0 && (
                          <div className="pnl-row sub-cost" style={!vatIncluded ? { opacity: 0.4, textDecoration: 'line-through' } : {}}>
                            <span className="pnl-label">{t('BTW on Import (21%)')}{!vatIncluded && ` ${t('(deductible)')}`}</span>
                            <span className="pnl-value">-{formatPnlPrice(car.customs_btw)}</span>
                          </div>
                        )}
                        {parseFloat(car.customs_broker) > 0 && (
                          <div className="pnl-row sub-cost">
                            <span className="pnl-label">{t('Customs Broker')}</span>
                            <span className="pnl-value">-{formatPnlPrice(car.customs_broker)}</span>
                          </div>
                        )}
                        {parseFloat(car.naks_cost) > 0 && (
                          <div className="pnl-row sub-cost">
                            <span className="pnl-label">{t('NAKS Type Approval')}</span>
                            <span className="pnl-value">-{formatPnlPrice(car.naks_cost)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {car.potential_vat_liability > 0 && (
                    <div className="pnl-row sub-cost">
                      <span className="pnl-label">{t('VAT liability (EU new vehicle)')}</span>
                      <span className="pnl-value">-{formatPnlPrice(car.potential_vat_liability)}</span>
                    </div>
                  )}

                  {/* VAT deduction line when excl-VAT mode */}
                  {!vatIncluded && vatDeductibleAmountRaw > 0 && (
                    <div className="pnl-row sub-cost" style={{ backgroundColor: 'rgba(20, 184, 166, 0.06)', borderRadius: '4px', padding: '0.3rem 0.5rem', margin: '0.25rem 0' }}>
                      <span className="pnl-label" style={{ color: '#14b8a6', fontWeight: '600' }}>{t('BTW Deduction (business)')}</span>
                      <span className="pnl-value" style={{ color: '#14b8a6', fontWeight: '600' }}>+{formatPnlPrice(vatDeductibleAmountRaw)}</span>
                    </div>
                  )}

                  <div className="pnl-row cost-subtotal">
                    <span className="pnl-label">{t('Total Costs')} {!vatIncluded ? t('(excl. BTW)') : ''}</span>
                    <span className="pnl-value">
                      -{formatPnlPrice(activeTotalCosts)}
                    </span>
                  </div>

                </div>
              </div>

              {/* Bottom line */}
              <div className="pnl-divider"></div>

              <div
                className="pnl-row profit-row"
                style={{
                  borderColor: pnlProfitColor,
                  backgroundColor: activeProfit < 0 ? 'rgba(225, 29, 72, 0.07)' : 'rgba(20, 184, 166, 0.08)'
                }}
              >
                <span className="pnl-label">
                  {t('Gross margin before discount & reserve')}
                  {!vatIncluded && hasVatDifference && <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '400' }}> {t('(excl. BTW)')}</span>}
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                  <span
                    className="pnl-profit-value"
                    style={{
                      color: pnlProfitColor,
                      fontSize: '1.3rem',
                      fontWeight: '700',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {activeProfit > 0 ? '+' : ''}{formatPnlPrice(activeProfit)}
                  </span>
                  {profitRangeVat && (
                    <span
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        opacity: 0.9,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <span style={{ color: '#64748b' }}>{t('Range:')}</span>
                      <span style={{
                        color: profitRangeVat.profitLo < 0 ? '#e11d48' : pnlProfitColor,
                        fontWeight: profitRangeVat.profitLo < 0 ? '600' : '500',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatPriceForRange(profitRangeVat.profitLo)}
                      </span>
                      <span
                        style={{
                          color: '#cbd5e1',
                          margin: '0 0.4rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: '1em',
                          opacity: 0.6
                        }}
                      >
                        <span style={{
                          display: 'inline-block',
                          width: '1.5px',
                          height: '6px',
                          backgroundColor: '#cbd5e1',
                          marginRight: '1.5px',
                          borderRadius: '0.5px'
                        }}></span>
                        <span style={{
                          display: 'inline-block',
                          width: '12px',
                          height: '1.5px',
                          backgroundColor: '#cbd5e1',
                          borderRadius: '0.5px'
                        }}></span>
                        <span style={{
                          display: 'inline-block',
                          width: '1.5px',
                          height: '6px',
                          backgroundColor: '#cbd5e1',
                          marginLeft: '1.5px',
                          borderRadius: '0.5px'
                        }}></span>
                      </span>
                      <span style={{
                        color: profitRangeVat.profitHi < 0 ? '#e11d48' : pnlProfitColor,
                        fontWeight: profitRangeVat.profitHi < 0 ? '600' : '500',
                        whiteSpace: 'nowrap'
                      }}>
                        {formatPriceForRange(profitRangeVat.profitHi)}
                      </span>
                    </span>
                  )}
                </div>
              </div>

              <div className="pnl-row" style={{ padding: '0.75rem 0', borderTop: '1px solid #e2e8f0', marginTop: '0.75rem' }}>
                <span className="pnl-label" style={{ fontWeight: '600', fontSize: '0.95rem' }}>{t('Margin Percentage')}</span>
                <span
                  className="pnl-value"
                  style={{
                    color: pnlProfitColor,
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'
                  }}
                >
                  {activeMarginPct > 0 ? '+' : ''}{activeMarginPct.toFixed(2)}%
                </span>
              </div>

              {/* From the gross margin to the expected profit and the profit if the sale disappoints. */}
              {netExpected !== null && <div className="pnl-net">
                <div className="pnl-row sub-cost">
                  <span className="pnl-label">{t('Negotiation room on the sale (4%)')}</span>
                  <span className="pnl-value">-{formatPnlPrice(saleHaircut)}</span>
                </div>
                {conditionReserve > 0 && (
                  <div className="pnl-row sub-cost">
                    <span className="pnl-label">{t('Reserve for unseen defects (older car)')}</span>
                    <span className="pnl-value">-{formatPnlPrice(conditionReserve)}</span>
                  </div>
                )}
                <div className="pnl-row profit-row" style={{ borderColor: netColor,
                  backgroundColor: netExpected < 0 ? 'rgba(225, 29, 72, 0.07)' : 'rgba(20, 184, 166, 0.08)' }}>
                  <span className="pnl-label">
                    {t('Expected profit')}
                    {!vatIncluded && hasVatDifference && <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '400' }}> {t('(excl. BTW)')}</span>}
                  </span>
                  <span className="pnl-profit-value" style={{ color: netColor, whiteSpace: 'nowrap' }}>
                    {netExpected > 0 ? '+' : ''}{formatPnlPrice(netExpected)}
                  </span>
                </div>
                {netSafe !== null && (
                  <div className="pnl-row" title={t('Profit if the car only sells at the low end of our Dutch value range, after the same costs and reserves.')}>
                    <span className="pnl-label">{t('If the sale disappoints')} ({t('sold at {price}', { price: eur(car.pred_sale_lo) })})</span>
                    <span className="pnl-value" style={{ color: netSafe < 0 ? '#e11d48' : '#0f766e' }}>
                      {netSafe > 0 ? '+' : ''}{formatPnlPrice(netSafe)}
                    </span>
                  </div>
                )}
              </div>}
            </div>
          ))}
        </div>

        {/* Collapsed: the planner is for someone who has already decided to look, so it costs
            one line until they ask for it. */}
        {economicsAvailable && (
          <details className="purchase-planner-fold">
            <summary>{t('Work out my own offer for this car')}</summary>
            <PurchasePlanner
              key={car.decision_snapshot_id || car.id}
              car={car}
              recommendationCurrent={recommendationCurrent}
            />
          </details>
        )}

        <p className="card-freshness">
          {freshness.dealerSourceCheck?.label || t('Dealer page not checked automatically')}
          {freshness.catalogueSnapshot && <> · {freshness.catalogueSnapshot.label}</>}
        </p>
      </div>

      <PricingExplanationPopup
        isOpen={showPricingPopup}
        onClose={() => setShowPricingPopup(false)}
        car={car}
      />
      <BpmExplanationPopup
        isOpen={showBpmPopup}
        onClose={() => setShowBpmPopup(false)}
        car={car}
      />
    </article>
  )
}

export default CarCard
